import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Layout } from "@/components/Layout";
import { LanguageToggle } from "@/components/LanguageToggle";
import { PRAT_T } from "@/data/praticien";
import { formatApiErrorDetail } from "@/components/praticien/PractitionerLogin";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPassword() {
  const [lang, setLang] = useState("fr");
  const [email, setEmail] = useState("");
  const [question, setQuestion] = useState(null);
  const [answer, setAnswer] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const t = PRAT_T[lang];

  const fetchQuestion = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/auth/recovery-question`, { params: { email } });
      if (!data.exists) { setError(t.errorGeneric); return; }
      setQuestion(lang === "es" ? data.question_es : data.question_fr);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail, t.errorGeneric));
    } finally {
      setLoading(false);
    }
  };

  const reset = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { email, answer, new_password: newPassword });
      setDone(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail, t.errorGeneric));
    } finally {
      setLoading(false);
    }
  };

  const q = lang === "es" ? question : question;

  return (
    <Layout>
      <div className="flex items-center justify-between mb-8 no-print">
        <Link to="/praticien" data-testid="prat-back-login" className="text-sm text-[var(--ink-soft)] hover:text-[var(--gold)] transition-colors">{t.backToLogin}</Link>
        <LanguageToggle lang={lang} onChange={setLang} />
      </div>
      <div className="max-w-md mx-auto animate-fade-up">
        <h1 className="font-serif-title text-4xl text-[var(--gold)] text-center mb-2">{t.forgotTitle}</h1>
        <p className="text-sm text-[var(--ink-soft)] text-center mb-8">{t.forgotSub}</p>

        {done ? (
          <div className="rounded-3xl bg-[var(--sand)] border border-[var(--border)] p-8 text-center" data-testid="prat-reset-success">
            <p className="text-[var(--ink)] mb-6">{t.resetSuccess}</p>
            <Link to="/praticien" className="inline-block px-8 py-3 rounded-full bg-[var(--gold)] text-white font-medium hover:brightness-105">{t.backToLogin}</Link>
          </div>
        ) : !question ? (
          <form onSubmit={fetchQuestion} className="rounded-3xl bg-[var(--sand)] border border-[var(--border)] p-6 sm:p-8 space-y-5">
            <div>
              <label htmlFor="fp-email" className="block text-sm text-[var(--ink)] mb-1.5 font-medium">{t.emailLabel}</label>
              <input id="fp-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="prat-forgot-email-input"
                className="w-full rounded-xl border border-[var(--border)] bg-white/80 p-3 focus:outline-none focus:border-[var(--gold)]" />
            </div>
            {error && <p className="text-sm text-[#B0553B]" data-testid="prat-forgot-error">{error}</p>}
            <button type="submit" disabled={loading} data-testid="prat-forgot-continue-btn"
              className={`w-full px-8 py-3.5 rounded-full font-medium transition-all ${loading ? "bg-[var(--beige)] text-[var(--ink-soft)]" : "bg-[var(--gold)] text-white hover:brightness-105"}`}>
              {t.getQuestion}
            </button>
          </form>
        ) : (
          <form onSubmit={reset} className="rounded-3xl bg-[var(--sand)] border border-[var(--border)] p-6 sm:p-8 space-y-5">
            <p className="text-sm text-[var(--ink)] font-medium" data-testid="prat-recovery-question">{q}</p>
            <div>
              <label htmlFor="fp-answer" className="block text-sm text-[var(--ink)] mb-1.5 font-medium">{t.answerLabel}</label>
              <input id="fp-answer" type="text" value={answer} onChange={(e) => setAnswer(e.target.value)} required data-testid="prat-answer-input"
                className="w-full rounded-xl border border-[var(--border)] bg-white/80 p-3 focus:outline-none focus:border-[var(--gold)]" />
            </div>
            <div>
              <label htmlFor="fp-newpw" className="block text-sm text-[var(--ink)] mb-1.5 font-medium">{t.newPasswordLabel}</label>
              <input id="fp-newpw" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required minLength={6} data-testid="prat-newpassword-input"
                className="w-full rounded-xl border border-[var(--border)] bg-white/80 p-3 focus:outline-none focus:border-[var(--gold)]" />
            </div>
            {error && <p className="text-sm text-[#B0553B]" data-testid="prat-reset-error">{error}</p>}
            <button type="submit" disabled={loading} data-testid="prat-reset-btn"
              className={`w-full px-8 py-3.5 rounded-full font-medium transition-all ${loading ? "bg-[var(--beige)] text-[var(--ink-soft)]" : "bg-[var(--gold)] text-white hover:brightness-105"}`}>
              {loading ? t.resetting : t.resetBtn}
            </button>
          </form>
        )}
      </div>
    </Layout>
  );
}
